import { AstraChip } from '~/astra'
import { PractitionerChipWithTooltip } from '~/components'
import {
    Include,
    LocationScheduleActivityLog,
    PractitionerScheduleActivityLog,
    PractitionerScheduleLocationActivityLog,
    PractitionerScheduleStatusActivityLog,
    useLocation,
    usePractitioner,
} from '~/storm'

import { BaseActivityLog, DateLocationCell, DatePractitionerCell, isDatePractitionerCell, useActivityLogs } from '../../../shared'
import { LocationScheduleActivityLogEntry } from './LocationScheduleActivityLogEntry'
import { PractitionerScheduleActivityLogEntry } from './PractitionerScheduleActivityLogEntry'
import { PractitionerScheduleLocationActivityLogEntry } from './PractitionerScheduleLocationActivityLogEntry'
import { PractitionerScheduleStatusActivityLogEntry } from './PractitionerScheduleStatusActivityLogEntry'

type LogEntryProps = {
    activityLogEntry:
        | PractitionerScheduleActivityLog
        | Include<PractitionerScheduleStatusActivityLog, { statusDefinition: true }>
        | Include<PractitionerScheduleLocationActivityLog, { location: true }>
        | LocationScheduleActivityLog
    selectedCell: DatePractitionerCell | DateLocationCell
}

const ActivityLogEntry = ({ activityLogEntry, selectedCell }: LogEntryProps) => {
    const pageView = isDatePractitionerCell(selectedCell) ? 'practitioner' : 'location'
    switch (activityLogEntry.entity_type) {
        case 'practitioner_schedule_activity_log':
            return <PractitionerScheduleActivityLogEntry activityLogEntry={activityLogEntry} />
        case 'location_schedule_activity_log':
            return <LocationScheduleActivityLogEntry activityLogEntry={activityLogEntry} />
        case 'practitioner_schedule_status_activity_log':
            return <PractitionerScheduleStatusActivityLogEntry statusLogEntry={activityLogEntry} />
        case 'practitioner_schedule_location_activity_log':
            return <PractitionerScheduleLocationActivityLogEntry locationLogEntry={activityLogEntry} pageView={pageView} />
        default:
            return null
    }
}

type Props = {
    cell: DatePractitionerCell | DateLocationCell
    hideLog: () => void
    isLoading: boolean
}

export const ActivityLog = ({ cell, hideLog, isLoading }: Props) => {
    const isPractitionerCell = isDatePractitionerCell(cell)
    const practitioner = usePractitioner({ where: { id: isPractitionerCell ? cell.practitionerId : -1 } })
    const location = useLocation({ where: { id: isPractitionerCell ? -1 : cell.locationId } })

    const activityLogs = useActivityLogs(cell)

    const afterTitle = isPractitionerCell ? (
        <PractitionerChipWithTooltip shortName={practitioner?.short_name ?? ''} size="medium" />
    ) : (
        <AstraChip variant="outlined" label={location?.room_code ?? ''} size="medium" />
    )

    return (
        <BaseActivityLog date={cell.date} onClose={hideLog} isLoading={isLoading} afterTitle={afterTitle}>
            {activityLogs.map(activityLog => (
                <ActivityLogEntry key={`${activityLog.entity_type}-${activityLog.id}`} activityLogEntry={activityLog} selectedCell={cell} />
            ))}
        </BaseActivityLog>
    )
}
